import React, {createContext, useContext, useState, useCallback, useMemo, useEffect} from 'react';

const SidebarContext = createContext({
    isCollapsed: false,
    toggleSidebar: () => {},
    sidebarWidth: 240,
    isMobile: false,
    isMobileOpen: false,
    closeMobileSidebar: () => {},
});

export const SIDEBAR_COLLAPSED_KEY = 'dashbo_sidebar_collapsed';
const BREAKPOINT_MOBILE = 768;
const BREAKPOINT_TABLET = 1100;

export const RANGE_MOBILE = 'mobile';
export const RANGE_TABLET = 'tablet';
export const RANGE_DESKTOP = 'desktop';

/** Preferencia guardada del usuario. Sólo la escribe el toggle en desktop. */
export function readCollapsedPreference() {
    try {
        return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
        return false;
    }
}

/** Rango de viewport: es quien decide el ancho del sidebar y si el usuario manda sobre él. */
export function viewportRange(width = window.innerWidth) {
    if (width < BREAKPOINT_MOBILE) return RANGE_MOBILE;
    if (width < BREAKPOINT_TABLET) return RANGE_TABLET;
    return RANGE_DESKTOP;
}

// El provider vive dentro del marco (`AppChromeV2`), que se rearma en CADA navegación: nada que
// guarde un `useRef` sobrevive de una pantalla a la siguiente. Sin esta memoria, la regla "en
// tablet va colapsado" se volvía a aplicar al montar cada pantalla — el usuario expandía la barra,
// clickeaba cualquier cosa y la encontraba cerrada otra vez.
//
// NO va a `localStorage` a propósito: en tablet el colapso es una imposición del ancho y no una
// preferencia que valga la pena recordar para la próxima sesión (por eso el toggle tampoco la
// escribe). Lo que tiene que sobrevivir es la elección DENTRO de esta sesión, y eso muere con la
// pestaña.
const sessionMemory = {range: null, collapsed: null};

/** Sólo para los tests: la memoria vive en el módulo y de otro modo se filtra entre casos. */
export function resetSidebarSessionMemory() {
    sessionMemory.range = null;
    sessionMemory.collapsed = null;
}

/**
 * Con qué ancho arranca el sidebar. Si ya hubo una elección en este rango, gana esa; si no, manda
 * la regla del rango. Se calcula acá y no en el efecto porque el efecto corre DESPUÉS del primer
 * pintado: dejarlo sólo ahí hacía que en tablet cada pantalla dibujara la barra ancha y la
 * encogiera enseguida.
 */
function initialCollapsed() {
    const range = viewportRange();
    if (sessionMemory.range === range && sessionMemory.collapsed !== null) {
        return sessionMemory.collapsed;
    }
    return range === RANGE_TABLET ? true : readCollapsedPreference();
}

export function SidebarProvider({children}) {
    const [isCollapsed, setIsCollapsedState] = useState(initialCollapsed);
    const [isMobile, setIsMobile] = useState(() => viewportRange() === RANGE_MOBILE);
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    // Toda escritura del colapso pasa por acá: si la memoria de sesión queda desfasada, el próximo
    // montaje —o sea, la próxima pantalla— la contradice.
    const setIsCollapsed = useCallback((next) => {
        setIsCollapsedState(prev => {
            const value = typeof next === 'function' ? next(prev) : next;
            sessionMemory.collapsed = value;
            return value;
        });
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const range = viewportRange();
            setIsMobile(range === RANGE_MOBILE);

            // Sin este corte, la regla "en tablet el sidebar va colapsado" se re-aplicaba en CADA
            // evento de resize: el usuario lo expandía y volvía a cerrarse al mover un píxel el
            // borde de la ventana, al rotar la tablet o al abrir las devtools. La regla es del
            // momento en que se ENTRA al rango; adentro, manda el usuario. El rango vive en la
            // memoria de módulo, no en un ref: el provider remonta con cada pantalla.
            if (range === sessionMemory.range) return;
            sessionMemory.range = range;

            if (range === RANGE_MOBILE) {
                setIsMobileOpen(false);
            } else if (range === RANGE_TABLET) {
                setIsCollapsed(true);
            } else {
                setIsCollapsed(readCollapsedPreference());
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setIsCollapsed]);

    const toggleSidebar = useCallback(() => {
        if (isMobile) {
            setIsMobileOpen(prev => !prev);
            return;
        }
        setIsCollapsed(prev => {
            const next = !prev;
            // La preferencia se guarda sólo en desktop: en tablet el colapso es una imposición del
            // ancho de pantalla, no una elección que valga la pena recordar para la próxima sesión.
            if (viewportRange() === RANGE_DESKTOP) {
                try {
                    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
                } catch { /* ignore */ }
            }
            return next;
        });
    }, [isMobile]);

    const closeMobileSidebar = useCallback(() => {
        setIsMobileOpen(false);
    }, []);

    const sidebarWidth = isMobile ? 0 : (isCollapsed ? 56 : 240);

    const value = useMemo(() => ({
        isCollapsed,
        toggleSidebar,
        sidebarWidth,
        isMobile,
        isMobileOpen,
        closeMobileSidebar,
    }), [isCollapsed, toggleSidebar, sidebarWidth, isMobile, isMobileOpen, closeMobileSidebar]);

    return (
        <SidebarContext.Provider value={value}>
            {children}
        </SidebarContext.Provider>
    );
}

export const useSidebar = () => useContext(SidebarContext);
